import React, { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "reactstrap";
import { useAssignNumber } from "src/numbers/hooks/useAssignNumber";
import type { ResponseError } from "src/shared/actions/objects/ResponseError";
import { ReactQueryResult } from "src/shared/components/ReactQueryResult";

export interface IMerchantFormNumberProvisioningAssignProps {
  countryCode: string;
  onNumberAssign: (no: string) => void;
  disabled?: boolean;
}

export const MerchantFormNumberProvisioningAssign = ({
  countryCode,
  onNumberAssign,
  disabled,
}: IMerchantFormNumberProvisioningAssignProps) => {
  const mutationResult = useAssignNumber(countryCode);
  const { mutateAsync } = mutationResult;

  const handleAssignClick = useCallback(async () => {
    try {
      const { mobileVirtualNumber } = await mutateAsync({});
      onNumberAssign(mobileVirtualNumber);
    } catch (ex) {
      // handled in <ReactQueryResult />
    }
  }, [mutateAsync, onNumberAssign]);

  const assignNumberError = useMemo(() => {
    if (mutationResult.isError) {
      const response = (mutationResult.error as ResponseError)?.response;
      if (response?.status === 404) {
        return (
          <FormattedMessage id="merchants.validation.numberProvisioning.noAvailableNumber" />
        );
      }
    }

    return null;
  }, [mutationResult.error, mutationResult.isError]);

  return (
    <>
      <p>
        <Button
          type="button"
          color="primary"
          onClick={handleAssignClick}
          disabled={disabled}
        >
          <FormattedMessage id="merchants.form.numberProvisioning.assignNumber" />
        </Button>
      </p>
      <ReactQueryResult {...mutationResult} error={assignNumberError} />
    </>
  );
};
